import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, ExternalLink, Pencil, Send } from "lucide-react";
import {
  listingsApi,
  type BuyerSubmissionSummaryDto,
  type ListingAgentDto,
  type ListingDetailDto,
} from "@/api/buyers";
import {
  EntityTabBar,
  CROSS_CUTTING_TAB_ICONS,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import {
  Button,
  EmptyState,
  MissingValue,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
  Skeleton,
  StatusBadge,
  Surface,
  TableValue,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { safeHref } from "@/design-system/lib/safeHref";
import {
  listingDisplayName,
  listingShortName,
  shortenListingName,
} from "@/lib/listingDisplay";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { useOptionalAuth } from "@/hooks/useAuth";
import { buyerLabel } from "./buyerFilters";
import { submissionStatusTone } from "./buyerListBits";
import { DetailField, DetailFieldList, DetailSection } from "./detailBits";
import { hasNoEmail, MissingEmailChip } from "./ListingAgentDirectory";
import { ListingAgentSectionModal } from "./listing/ListingAgentSectionModal";
import { ListingContactFormModal } from "./listing/ListingContactFormModal";
import { AgentResearchEntry } from "./listing/agentResearch/AgentResearchEntry";
import { ListingSectionEditModal } from "./listing/ListingSectionEditModal";
import { listingSection, type ListingSectionId } from "./listing/listingSectionConfig";
import {
  SellerPrequalificationSurface,
  useSellerPrequalAdapterForListing,
} from "./prequalification/SellerPrequalificationSurface";
import { PresentBuyerWizard } from "./presentBuyer/PresentBuyerWizard";
import { RecordSubmissionsTable, useRecordSubmissions } from "./submission/recordSubmissions";
import {
  ActionsColumnHeader,
  ShowArchivedToggle,
  SubmissionRowMenu,
  useSubmissionRemoval,
  withArchivedRows,
} from "./submission/submissionArchive";
import { isNotFoundError, QueryErrorState } from "./queryStates";

export type TabKey =
  | "overview"
  | "buyers-presented"
  | "seller-prequalification"
  | "calendar"
  | "documents"
  | "tasks"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

/**
 * The tab strip, in display order.
 *
 * <p>Exported so the full-page route and the drawer that renders the same body
 * cannot disagree about which tabs exist — a second hand-written copy is how
 * one surface quietly grows a tab the other does not have.</p>
 */
export const LISTING_TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  // The listing's own daily driver — who has been put in front of this
  // business. `Send` mirrors the buyer record's Submissions tab: it is the same
  // relationship read from the other end.
  { key: "buyers-presented", label: "Buyers presented", icon: Send },
  // 🔴 THE SELLER'S PREQUALIFICATION LIVES HERE, on the record it describes.
  //
  // It answers whether this BUSINESS's seller can carry a note, so it is a fact
  // about the listing — the pointer has always been written to the listing row,
  // and only the addressing was submission-shaped, because a submission was
  // where the first surface happened to be built. Reaching it meant opening one
  // buyer's submission and editing a record that serves all of them.
  //
  // Adjacent to Buyers presented because the two are the listing's two halves of
  // one question: who is chasing this business, and whether the person selling
  // it can actually finance the way they are being chased. Entity-specific, so
  // it slots before the cross-cutting block — Layout Standard §4.2, which is
  // explicit that a module may add a tab post-v1 without amending the standard
  // provided it slots correctly. Nothing already in the strip moved.
  //
  // The submission side keeps a working panel on its Escalation tab: that
  // adjacency is behavioural (the escalation gate WARNS when this is missing, so
  // the control that clears the warning has to be what the eye lands on next),
  // and it is genuinely where an invitation sent from there came from.
  { key: "seller-prequalification", label: "Seller prequalification", icon: BadgeCheck },
  // 🔴 The cross-cutting SEVEN last, in Layout Standard §3 canonical order —
  // Calendar now named explicitly in it, between Tasks and Messages.
  //
  // Calendar used to sit above this line, as the last entity-specific tab. §3.2
  // (v1.2) recorded two readings of §3 and deferred the choice; v1.5 resolves it
  // by arithmetic rather than preference. 18 entity detail pages already put
  // Calendar between Tasks and Messages and two did not — this page and
  // BuyerSubmission. Moving those two closes the seam at 20/20; moving the other
  // way would have moved 18 pages and left 17 disagreeing. (Looking for a
  // "v1.3"? The number was reserved for this and then overtaken by the shipped
  // v1.4, so the resolution is in v1.5.)
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  {
    key: "communications",
    label: "Communications",
    icon: CROSS_CUTTING_TAB_ICONS.communications,
  },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];

const TAB_KEYS = new Set<string>(LISTING_TAB_ITEMS.map((t) => t.key));

/**
 * One parse rule for both surfaces.
 *
 * <p>The page reads the tab out of the URL and the drawer may deep-link into
 * one too; if each wrote its own membership test, an unknown value could fall
 * back to Overview in one place and render an empty page in the other.</p>
 */
export function isListingTabKey(raw: string): raw is TabKey {
  return TAB_KEYS.has(raw);
}

/**
 * What this listing is called in the CHROME — page title, breadcrumb leaf,
 * drawer heading, and the label the messaging composer attaches.
 *
 * <p>Now a thin adapter over the ONE ladder rather than its own: this page
 * walked businessName → headline while the submission surfaces walked
 * businessName alone and the messaging chip walked businessName → raw URL, so a
 * blind listing read three different ways depending on where you met it. Prefers
 * the server's own answer and falls back to deriving it, which keeps the title
 * correct for a payload cached before `displayName` existed.</p>
 *
 * <p><b>SHORT.</b> Every slot this feeds is a heading. A marketplace title
 * carries its pitch — "…ATM Portfolio | $820K Annual Cash Flow" — and the whole
 * string ran the drawer card to about ten lines. {@link listingFullTitle} still
 * has the pitch for the places that want it.</p>
 */
export function listingTitle(listing: ListingDetailDto): string {
  return shortenListingName(listing.displayName) ?? listingShortName(listing);
}

/**
 * The same name with its pitch intact.
 *
 * <p>🔴 The DMS root-name HINT must use this one. `EnsureEntityTree` resolves
 * the folder name server-side through `BuyerSubmissionFolderNaming` — reproduced
 * in T-SQL by an applied migration and pinned by a parity test, so it is frozen —
 * and that answer wins. The hint only matters in the pathological case where it
 * does not, and a hint shorter than the frozen policy would name one folder
 * differently from every other.</p>
 *
 * <p>Also the tooltip beside a shortened heading, which is how the pitch stays
 * reachable rather than merely retained.</p>
 */
export function listingFullTitle(listing: ListingDetailDto): string {
  return listing.displayName?.trim() || listingDisplayName(listing);
}

export interface ListingDetailBodyProps {
  listingId: string;
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;

  /**
   * Open the present-a-buyer wizard, when the SURFACE above owns it.
   *
   * <p>The full page lifts the wizard into its header so "Present a buyer" is
   * the page's gold primary and is reachable from every tab, not only from the
   * one tab that lists the results. Absent — which is the drawer — the body
   * falls back to mounting the wizard itself, so a listing glanced at from a
   * buyer's shortlist keeps the action rather than losing it to a header that
   * is not there.</p>
   */
  onPresent?: () => void;

  /**
   * Open one submission as a RECORD, when the surface above owns the drawer.
   *
   * <p>🔴 The parameter belongs to the page, never to this body. `?submission=`
   * is URL state, and a body that wrote to it from inside a drawer would rewrite
   * the address bar of the page underneath — and collide with the buyer page's
   * own `?submission=` drawer, which reads the same key. So the body reports the
   * click and the caller decides where the record opens; the same split
   * `onPresent` uses, and the same one that keeps the tab out of here.</p>
   */
  onOpenSubmission?: (submissionId: string, buyerName: string) => void;
}

/**
 * One listing, tabbed.
 *
 * <p>🔴 <b>This page used to say, in a comment, that a listing "is not
 * registered as an owner in Notes / Tasks / Documents / Communications" and so
 * deliberately had no cross-cutting tabs. That is no longer true.</b> A listing
 * is now a registered owner on every one of those surfaces, and the tabs are
 * here.</p>
 *
 * <p>It is still a SHARED catalog record, and that is the whole point rather
 * than an objection to it: its notes, tasks, documents and conversations are
 * shared across every buyer being worked against it. The cross-cutting systems
 * already scope by owner type and id — never by the acting specialist — so
 * nothing here narrows them further. A note about the seller's timeline is a
 * fact about the listing, and hiding it from the next specialist to open the
 * record is how two people ask the broker the same question twice.</p>
 *
 * <p>The per-buyer negotiation still belongs on the submission, which is where
 * one buyer's conversation about this listing lives.</p>
 *
 * <p>Tab state is the CALLER's: the route wrapper keeps it in the URL, and the
 * drawer that renders this same body holds it locally. That is why this
 * component never touches `useSearchParams` — a body that reached for the URL
 * would rewrite the address bar from inside a drawer.</p>
 */
export function ListingDetailBody({
  listingId,
  activeTab,
  onTabChange,
  onPresent,
  onOpenSubmission,
}: ListingDetailBodyProps) {
  // Self-gating via `useOptionalAuth` — the ConvertToBorrowerButton precedent.
  // No auth context resolved = not permitted, which fails closed.
  const auth = useOptionalAuth();
  // Presenting a buyer is a SUBMISSION write, not a listing one — the same
  // permission the submissions list gates its "Present a buyer" button on.
  const canPresent = Boolean(auth?.hasPermission("Submissions.Manage"));
  // Writing the seller prequalification IS a listing write — it lands on the
  // listing row, and this controller's own rule is that any holder of
  // Listings.Manage may maintain any listing, because two specialists working
  // the same listing must both be able to. Mirrors the server gate exactly, so
  // the button's enabled state and the server's refusal can never disagree.
  const canManageListing = Boolean(auth?.hasPermission("Listings.Manage"));
  // Only reached when the caller does NOT own the wizard — the drawer. On the
  // page this stays false forever and the header's copy is the single opener,
  // so the two can never both be mounted.
  const [presenting, setPresenting] = useState(false);
  const ownsWizard = !onPresent;

  const listing = useQuery({
    queryKey: listingKeys.detail(listingId),
    queryFn: () => listingsApi.get(listingId),
    enabled: Boolean(listingId),
  });

  if (listing.isLoading) return <Skeleton className="h-64 w-full" />;
  // 🔴 "It may have been removed" is a claim about the RECORD, and only a 404
  // licenses it. Every other failure — a 500, a dropped connection, a 403 — used
  // to render the same sentence about a listing that is sitting right there.
  if (listing.isError && !isNotFoundError(listing.error)) {
    return (
      <QueryErrorState
        what="this listing"
        onRetry={() => void listing.refetch()}
        retrying={listing.isFetching}
      />
    );
  }
  if (!listing.data) {
    return <EmptyState title="Listing not found" description="It may have been removed." />;
  }

  const l = listing.data;
  const title = listingTitle(l);

  return (
    <>
      <EntityTabBar
        ariaLabel="Listing sections"
        testIdPrefix="listing"
        items={LISTING_TAB_ITEMS}
        value={activeTab}
        onChange={onTabChange}
        className="mb-space-4"
      />

      {/* The DRAWER's copy of the wizard. Mounted outside the tab bodies so it
          survives a tab change, and prefilled with THIS listing with the buyer
          left open — the mirror of the buyer record's own entry point. The page
          mounts its own beside the header instead, so the wizard is reachable
          from a tab that does not list submissions. */}
      {canPresent && ownsWizard && (
        <PresentBuyerWizard
          open={presenting}
          onClose={() => setPresenting(false)}
          listingId={l.id}
          listingName={title}
          /*
            🔴 The worst instance of the navigate-away class, and the reason
            this prop is threaded rather than defaulted.

            This copy of the wizard only mounts when there is no host page above
            (`ownsWizard`) — which in practice means this listing is itself open
            as a drawer, over a buyer page. Navigating on completion would tear
            down that buyer page AND the drawer the specialist is standing in,
            to show them a submission for the listing they were just reading.
            That is the exact defect the buyers-presented table was converted
            away from three files over.

            The host tells us what it can do. `onOpenSubmission` is how this
            body already reports "show me this submission" to whoever is above
            it, so completion reuses it; with no such host we keep the
            navigate, which is correct at a bare route.
          */
          onCreated={
            onOpenSubmission
              ? (submissionId) => onOpenSubmission(submissionId, "")
              : undefined
          }
        />
      )}

      {/* 🔴 The per-section pencils ride the BODY, not the page, so they land on
          the full page and inside the record drawer alike — Layout Standard
          §4.1a.1 (v1.5), which narrows the Overview rule to WORKFLOW actions and
          says a correction to a field belongs where that field is displayed. */}
      {activeTab === "overview" && (
        <ListingOverview listing={l} canEdit={canManageListing} />
      )}
      {activeTab === "buyers-presented" && (
        <ListingSubmissionsTab
          listingId={listingId}
          onPresent={
            canPresent ? (onPresent ?? (() => setPresenting(true))) : undefined
          }
          onOpenSubmission={onOpenSubmission}
        />
      )}
      {activeTab === "seller-prequalification" && (
        <ListingSellerPrequalificationTab
          listingId={listingId}
          canEdit={canManageListing}
        />
      )}
      {/* 🔴 No permission gate on the panel's Schedule button, deliberately:
          all five Appointments.* permissions are absent from RolePermissionMap,
          so gating it the way Tasks gates its own would hide scheduling from
          every non-admin. AppointmentListMini gates on entityArchived alone. */}
      {activeTab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="Listing"
          relatedEntityId={listingId}
          entityLabel={title}
        />
      )}
      {/* 🔴 `rootName` takes the FULL name, not the shortened heading above. The
          server names this root through the frozen BuyerSubmissionFolderNaming
          policy and that answer wins; the hint only lands in the pathological
          case where it does not, and a short hint would name one folder
          differently from every other listing's. */}
      {activeTab === "documents" && (
        <DocumentPanel
          ownerType="Listing"
          ownerId={listingId}
          rootName={listingFullTitle(l)}
        />
      )}
      {activeTab === "tasks" && (
        <TaskListMini
          relatedEntityType="Listing"
          relatedEntityId={listingId}
          addButtonLabel="+ Add task linked to this listing"
        />
      )}
      {activeTab === "messages" && (
        <RelatedConversationsPanel
          resourceType="listing"
          resourceId={listingId}
          entityLabel={title}
        />
      )}
      {activeTab === "communications" && (
        <CommunicationsTabPanel ownerType="listing" ownerId={listingId} ownerLabel={title} />
      )}
      {activeTab === "notes" && <NotesTabPanel ownerType="listing" ownerId={listingId} />}
      {activeTab === "activity" && <ActivityTabPanel ownerType="listing" ownerId={listingId} />}
    </>
  );
}

/**
 * The record itself: what the business is, what it costs, who is selling it,
 * and where it came from.
 *
 * <p><b>Two columns, and the split is by QUESTION rather than by size.</b> The
 * main column answers "what is this business" — Business, Financials,
 * Description, and the note on the record. The rail answers "where did it come
 * from and who do we talk to" — the listing agent, and the provenance of the
 * record itself. A specialist deciding whether to present a buyer reads the
 * left; a specialist about to pick up the phone reads the right.</p>
 *
 * <p>⚠️ <b>Neither half collapses any more, and Description / Notes on file
 * became unconditional to make that true.</b> They used to render only when they
 * had content, which was defensible while the page had one whole-record Edit
 * button in its header — but a card that is absent carries no pencil, so on the
 * thinnest record the module can produce (a listing created from a pasted URL
 * alone) the description and the note would have been unreachable from the page
 * that owns them. An always-present card that says "Nothing recorded." is the
 * same information plus a door. It also strengthens the reason the two live in
 * the MAIN column rather than the rail: both halves now render on every record.</p>
 *
 * <p>🔴 <b>Six cards, six doors, and between them they cover every field the
 * page's old whole-record Edit form carried</b> — pinned by
 * <c>listing/ListingSectionEdit.test.tsx</c>, which asserts the sections' key set
 * against <c>EMPTY_LISTING_FORM</c>. That is what licensed removing the header
 * button rather than leaving two ways in.</p>
 */
function ListingOverview({
  listing: l,
  canEdit,
}: {
  listing: ListingDetailDto;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState<ListingSectionId | null>(null);
  const [editingAgent, setEditingAgent] = useState(false);
  const [editingContactForm, setEditingContactForm] = useState(false);

  /** No header row at all when the reader cannot write — never a greyed pencil.
   *  The `BuyerSectionCard` rule, and every other affordance on this page. */
  const pencil = (id: ListingSectionId, title: string) =>
    canEdit ? (
      <Button
        variant="text"
        size="sm"
        onClick={() => setEditing(id)}
        aria-label={`Edit ${title}`}
        data-testid={`listing-section-edit-${id}`}
      >
        <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
      </Button>
    ) : null;

  return (
    <>
    <ResponsiveSplit
      gap="lg"
      mainMinWidth="34rem"
      railWidth={RECORD_RAIL_WIDTH}
      railGrows={false}
      rail={
        <ListingRail
          l={l}
          canEdit={canEdit}
          pencil={pencil}
          setEditingAgent={setEditingAgent}
          setEditingContactForm={setEditingContactForm}
        />
      }
      main={
      <div className="grid gap-space-5">
        {/* 🔴 THE `xl:` HERE WAS A WORKAROUND FOR A PRIMITIVE, AND IT IS GONE.
            It read: "`DetailField` gives its label an 11rem fixed track, so at
            two-thirds of a `lg` viewport a two-up grid leaves the VALUE column
            narrower than the label beside it and every figure wraps." That was
            an accurate description of a real defect — and the fix was pushed
            out to the one page that noticed, as a viewport breakpoint, where it
            could not help the buyer or submission cards with the same problem.
            The label track is content-sized now, so the grid can ask its own
            container how wide it is and stop consulting the window. */}
        <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="lg">
          <DetailSection title="Business" action={pencil("business", "Business")}>
            <DetailFieldList>
              <DetailField label="Business name" value={l.businessName} wide />
              {/* `wide` mirrors `listingSectionConfig`, which marks both of
                  these wide for the edit modal. A 500px headline in a column
                  beside a 74px label set to six lines. */}
              <DetailField label="Headline" value={l.headline} wide />
              <DetailField label="Industry" value={l.industry} />
              <DetailField
                label="Location"
                value={[l.city, l.state, l.zip].filter(Boolean).join(", ")}
              />
              <DetailField label="Year established" value={l.yearEstablished} />
              <DetailField label="Employees" value={l.employeeCount} />
            </DetailFieldList>
          </DetailSection>

          <DetailSection title="Financials" action={pencil("financials", "Financials")}>
            <DetailFieldList>
              <DetailField label="Asking price" value={money(l.askingPrice)} />
              <DetailField label="Cash flow" value={money(l.cashFlow)} />
              <DetailField label="Revenue" value={money(l.revenue)} />
              <DetailField label="Inventory" value={money(l.inventory)} />
              <DetailField label="Real estate" value={money(l.realEstateValue)} />
            </DetailFieldList>
          </DetailSection>
        </ResponsiveGrid>

        <DetailSection title="Description" action={pencil("description", "Description")}>
          <div className="whitespace-pre-wrap text-small text-onyx">
            {l.description || <span className="text-gray-mid">Nothing recorded.</span>}
          </div>
        </DetailSection>

        {/*
          🔴 The free-text `notes` COLUMN on the listing record, which is a
          different thing from the Notes TAB on this same page — that one is the
          shared internal-note composer and feed every CRM record has. Titled
          apart so a specialist cannot type into one expecting the other to see
          it; the same split the buyer record settled on.
        */}
        <DetailSection title="Notes on file" action={pencil("notes", "Notes on file")}>
          <div className="text-caption text-gray-mid mb-space-2">
            The note field on this listing&apos;s record. Team discussion lives on
            the Notes tab.
          </div>
          <div className="whitespace-pre-wrap text-small text-onyx">
            {l.notes || <span className="text-gray-mid">Nothing recorded.</span>}
          </div>
        </DetailSection>
      </div>
      }
    />

      {/* Mounted only while open, so a closed modal costs no agent-list read and
          no render — the `BuyerDetailPage` convention. */}
      {canEdit && editing && (
        <ListingSectionEditModal
          section={listingSection(editing)}
          listing={l}
          open
          onClose={() => setEditing(null)}
        />
      )}
      {canEdit && editingAgent && (
        <ListingAgentSectionModal
          listing={l}
          open
          onClose={() => setEditingAgent(false)}
        />
      )}
      {canEdit && editingContactForm && (
        <ListingContactFormModal
          listing={l}
          open
          onClose={() => setEditingContactForm(false)}
        />
      )}
    </>
  );
}

/**
 * The right rail. Extracted so the body reads as one `ResponsiveSplit` call.
 */
function ListingRail({
  l,
  canEdit,
  pencil,
  setEditingAgent,
  setEditingContactForm,
}: {
  l: ListingDetailDto;
  canEdit: boolean;
  pencil: (id: ListingSectionId, title: string) => ReactNode;
  setEditingAgent: (open: boolean) => void;
  setEditingContactForm: (open: boolean) => void;
}) {
  return (
      <div className="grid gap-space-5">
        <ListingAgentPanel
          agent={l.listingAgent}
          contactFormUrl={l.contactFormUrl}
          onEditContactForm={canEdit ? () => setEditingContactForm(true) : undefined}
          research={
            <AgentResearchEntry listingId={l.id} hasAgent={Boolean(l.listingAgent)} canManage={canEdit} />
          }
          action={
            canEdit ? (
              <Button
                variant="text"
                size="sm"
                onClick={() => setEditingAgent(true)}
                aria-label="Edit Listing agent"
                data-testid="listing-section-edit-agent"
              >
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
            ) : null
          }
        />

        <DetailSection title="Source" action={pencil("source", "Source")}>
          <DetailFieldList>
          <DetailField label="Marketplace" value={l.marketplaceName} />
          <DetailField label="Listing reference" value={l.listingReference} />
          <DetailField
            label="URL"
            wide
            value={<ListingUrlValue rawUrl={l.rawUrl} />}
          />
          {/*
            The dedupe comparison key, shown deliberately. A specialist told
            "this looks like a duplicate" can only agree or disagree if they
            can see WHAT was compared — an opaque warning is unarguable.

            🔴 It is READ-ONLY here and inside the Source modal: it is derived
            server-side from the listing URL and is what duplicate detection
            compares on, so a hand-edited one would silently stop colliding with
            the listings it is meant to catch. `createdAt` is write-once.
          */}
          <DetailField label="Dedupe key" value={l.normalizedUrl} wide />
          <DetailField label="Added" value={<Timestamp value={l.createdAt} />} />
          </DetailFieldList>
        </DetailSection>
      </div>
  );
}

function ListingAgentPanel({
  agent,
  action,
  contactFormUrl,
  onEditContactForm,
  research,
}: {
  agent?: ListingAgentDto | null;
  action?: ReactNode;
  /** D7 / D18 — the research door and card line (links only). */
  research?: ReactNode;
  /** D20 — the listing broker's web contact form, a fact about the LISTING. */
  contactFormUrl?: string | null;
  /** Absent when the reader cannot write — never a greyed control. */
  onEditContactForm?: () => void;
}) {
  const contactForm = (
    <ContactFormField url={contactFormUrl} onEdit={onEditContactForm} />
  );

  if (!agent) {
    return (
      <DetailSection title="Listing agent" action={action}>
        <div className="text-small text-gray-mid">
          No agent recorded. A listing without one can still be worked — it just
          has no named contact on the seller's side.
        </div>
        <DetailFieldList>{contactForm}</DetailFieldList>
        {research}
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Listing agent" action={action}>
      <DetailFieldList>
      {/* D19 — a firm contact's label is the server's "Firm contact — {Firm}". */}
      <DetailField label={agent.isFirmContact ? "Contact" : "Name"} value={agent.displayName} />
      <DetailField label="Firm" value={agent.firmName} />
      <DetailField label="Title" value={agent.title} />
      {/*
        🔴 A missing email is not a blank field here, it is a defect notice.
        Agents are shared records the server get-or-creates BY EMAIL, so one
        without an address is invisible to the dedupe probe: the next pasted
        listing naming this agent will mint a SECOND record rather than find
        this one, and the two will then each carry part of the listing count.
        An em dash said none of that.
      */}
      <DetailField
        label="Email"
        value={hasNoEmail(agent) ? <MissingEmailChip /> : agent.email}
      />
      <DetailField label="Phone" value={phoneWithType(agent)} />
      {contactForm}
      <DetailField label="Listings" value={agent.listingCount} />
      {/*
        The one seam this module opens into the Leads subsystem: a listing agent
        who has been escalated into broker recruitment. Shown here so the two
        records are never worked as if they were unconnected.
      */}
      {agent.escalatedToLeadId ? (
        <DetailField
          label="Recruitment"
          value={
            <span>
              Escalated to a broker lead{" "}
              <BrokerLeadLink leadId={agent.escalatedToLeadId} />
              {agent.escalatedAtUtc && (
                <span className="text-gray-mid">
                  {" · "}
                  <Timestamp value={agent.escalatedAtUtc} />
                </span>
              )}
            </span>
          }
        />
      ) : (
        /*
          🔴 READ-ONLY, and the door is on the header — Layout Standard §4.1a.
          This used to carry "Escalate into broker recruitment", which MINTS A
          BROKER LEAD: a create, on the surface a reader lands on by default, and
          inside the record drawer that opens over a buyer's shortlist. An
          Overview may summarise, reference and link; it may not mutate. The
          header is exempt as persistent chrome, and that is where it went — so
          it is still one press away for anyone on the page, and absent from the
          drawer, which is the point rather than a side effect.

          The sentence stays, because it is a FACT about this listing that a
          specialist needs before they promise a buyer anything.
        */
        <DetailField
          label="Recruitment"
          wide
          value={
            <span className="text-gray-mid">
              Not yet in broker recruitment. A submission cannot be escalated into
              a deal until this agent is onboarded as a Phoenix broker.
            </span>
          }
        />
      )}
      </DetailFieldList>
      {research}
    </DetailSection>
  );
}

/**
 * The way to the broker lead this agent became.
 *
 * <p>🔴 <b>A new tab, not a `Link`.</b> This body renders inside a drawer as
 * often as it renders as a page — a buyer's shortlist opens a listing over the
 * buyer they are working — and a router navigation from in there unmounts the
 * host page and takes the drawer, the buyer's tab and their place in the queue
 * with it. The lead lives in a different module with no record drawer of its
 * own, so the honest way to reach it without throwing the workspace away is to
 * open it beside this one. Same reason the original-listing link on this page
 * has always done it.</p>
 */
function BrokerLeadLink({ leadId }: { leadId: string }) {
  return (
    <a
      href={`/leads/${leadId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-space-1 text-gold-shadow hover:underline"
    >
      Open the lead
      <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
    </a>
  );
}

/**
 * Escalate this agent into broker recruitment.
 *
 * <p>🔴 <b>The action this UI was missing.</b> The endpoint has existed since
 * the Buyer CRM shipped, but with no client function and no button it was
 * reachable only by hand — and it was double-gated in a way that made it
 * unreachable outside ADMIN anyway. Escalation PR-2 fixes the permission and
 * adds this, because escalating a submission into a deal REQUIRES the agent to
 * be a payable Broker first: shipping a gate nobody can satisfy is the same
 * defect as an empty state naming an action the UI does not offer.</p>
 *
 * <p>It creates a BrokerLead for a Broker Marketer to work. It does NOT create
 * a Broker — that stays a deliberate second act by someone else.</p>
 *
 * <p>🔴 <b>It hands back what it made.</b> The endpoint returns the updated
 * agent carrying the new lead's id, and that answer used to be discarded: the
 * specialist got a toast naming a record they had no way to reach, and the link
 * appeared only if and when the background refetch landed. Escalating is a
 * hand-off, and a hand-off that does not say where the work went is the same
 * dead end as an empty state naming an action the page does not offer.</p>
 */

/**
 * Whether this business's seller can carry a note — the record's own tab.
 *
 * <p>The SAME surface the submission's Escalation tab mounts, with the listing
 * adapter instead of the submission one: same rows, same lifecycle vocabulary,
 * same three-state read, different endpoints and a different gate. See
 * <c>SellerPrequalificationSurface</c> for why the two addresses are not
 * interchangeable.</p>
 *
 * <p>No <c>intro</c>: the submission mount needs one because it has to explain
 * that the fact belongs somewhere else and reaches other buyers. Standing on the
 * listing, that is simply where you are, and saying it would be noise. This tab
 * carries the caption below instead, which is the thing a listing reader
 * genuinely does not know — how many buyers a change reaches.</p>
 */
function ListingSellerPrequalificationTab({
  listingId,
  canEdit,
}: {
  listingId: string;
  canEdit: boolean;
}) {
  const adapter = useSellerPrequalAdapterForListing(listingId);
  const l = adapter.status.data;

  // 🔴 `submissionCount`, NOT `otherSubmissionCount`. "Other" is relative to an
  // addressed submission and there is none here, so that member carries no
  // information this surface should print — the DTO says which member each
  // address renders. Absent on a payload cached before the field existed, which
  // is why the caption is conditional rather than defaulting to zero: "0 buyers"
  // is a claim, and a stale cache is no evidence for it.
  const count = l?.submissionCount;

  return (
    <div className="space-y-space-4">
      <p className="m-0 text-caption text-gray-mid">
        Whether Phoenix has established that this seller can carry a note on
        these terms. It is a fact about the business, so it is recorded here
        rather than against one buyer
        {count != null && count > 0
          ? ` — and it serves all ${count} ${count === 1 ? "buyer" : "buyers"} submitted against this listing.`
          : "."}
      </p>

      <SellerPrequalificationSurface
        adapter={adapter}
        canEdit={canEdit}
        testId="listing-seller-prequalification"
      />
    </div>
  );
}

/**
 * Who has been put in front of this listing.
 *
 * <p>The one-listing slice of the submissions list. It was a card on the old
 * flat page; as a tab it drops the section heading the tab label now carries,
 * which is also why a failed read renders the BLOCK error rather than the
 * inline one — the inline variant exists for a section that still has a
 * heading above it.</p>
 */
function ListingSubmissionsTab({
  listingId,
  onPresent,
  onOpenSubmission,
}: {
  listingId: string;
  /** Absent when this reader may not present — the empty state's copy names the
   *  action, so the button that satisfies it has to be here or nowhere. */
  onPresent?: () => void;
  /** Present when the surface above hosts a record drawer. Absent — the drawer,
   *  which has no second drawer slot — a row opens in a new tab instead. */
  onOpenSubmission?: (submissionId: string, buyerName: string) => void;
}) {
  // D4 / R4 — "Show archived" is LOCAL here: this body renders in a drawer as
  // often as on a page and reads no router hook, so it cannot own a link key.
  const [includeArchived, setIncludeArchived] = useState(false);
  const list = useRecordSubmissions({ listingId }, { includeArchived });
  const rows = list.rows;
  // D4 — an archived row here offers Restore in its •••; this table gains no
  // other archive or delete action (it is not a D3 surface).
  const removal = useSubmissionRemoval();
  const toggle = (
    <ShowArchivedToggle
      checked={includeArchived}
      onChange={setIncludeArchived}
      testId="listing-show-archived"
    />
  );

  if (list.isLoading) return <Skeleton className="h-40 w-full" />;

  // The error branch goes BEFORE the empty branch: "No buyers have been
  // presented" is a claim about this listing's history, and a failed read is no
  // evidence for it. Acting on it means presenting a buyer who may already be
  // in play.
  if (list.isError) {
    return (
      <QueryErrorState
        what="the buyers presented to this listing"
        onRetry={list.refetch}
        retrying={list.isFetching}
      />
    );
  }

  if (rows.length === 0) {
    /*
      🔴 The empty state named an action the page did not offer: "no buyers
      have been presented yet" with nowhere to present one from. The control is
      here now, gated — an action offered to somebody who will be refused is the
      same defect wearing the other face.

      It opens the guided wizard IN PLACE with this listing prefilled, rather
      than deep-linking to the submissions list: a hand-off that makes the
      specialist find the listing again is the dead end in a politer form.
    */
    return (
      <>
        <div className="flex justify-end mb-space-2">{toggle}</div>
        {list.hasHiddenArchived ? (
          <EmptyState
            title="Every submission is archived"
            description="Turn on Show archived to see the buyers presented."
          />
        ) : (
          <EmptyState
            title="No buyers presented yet"
            description="Nobody has been put in front of this listing's broker."
            action={onPresent ? { label: "Present a buyer", onClick: onPresent } : undefined}
          />
        )}
      </>
    );
  }

  return (
    /* No `overflow-x-auto` here — `Table` brings its own scroll wrapper, and
       nesting a second one gives a wide table two scrollbars. */
    <div className="min-w-0">
      {/* The action is not only available while the list is empty — a listing
          worth presenting once is worth presenting again. */}
      <div className="flex flex-wrap items-center justify-end gap-space-3 mb-space-2">
        {toggle}
        {onPresent && (
          <Button
            size="sm"
            variant="secondary"
            onClick={onPresent}
            data-testid="listing-present-buyer"
          >
            Present a buyer
          </Button>
        )}
      </div>
      <Surface kind="card">
        <RecordSubmissionsTable
          state={list}
          caption="Buyers presented"
          sortLabel="Sort the buyers presented by"
          testId="listing-submissions-table"
          columns={withArchivedRows(
            [
              ...listingSubmissionColumns(onOpenSubmission),
              {
                key: "actions",
                header: <ActionsColumnHeader />,
                align: "right" as const,
                render: (s: BuyerSubmissionSummaryDto) =>
                  s.isArchived ? (
                    <SubmissionRowMenu
                      removal={removal}
                      target={{
                        id: s.id,
                        listingName: s.buyerStaffDisplayName ?? s.buyerDisplayName,
                        isArchived: true,
                        canArchive: s.canArchive,
                      }}
                    />
                  ) : null,
              },
            ],
            { pillColumnKey: "status", keepKeys: ["actions"] },
          )}
        />
      </Surface>
      {removal.node}
    </div>
  );
}

/**
 * The buyers presented to a listing, as columns.
 *
 * 🔴 THE ROWS STAY RICH, AND DELIBERATELY DIFFER FROM THE BUYER'S OWN TABLE.
 * Headed by the BUYER, because that is what distinguishes one row from another
 * here, and carrying no asking price — every row on a listing would repeat the
 * same one. Shared chrome governs how a table looks, not what a row may say.
 */
function listingSubmissionColumns(
  onOpenSubmission?: (submissionId: string, buyerName: string) => void,
): SubTableColumn<BuyerSubmissionSummaryDto>[] {
  return [
    {
      /*
        "Submission", not "Buyer". The cell is NAMED for the buyer because that
        is what distinguishes one row from another on a listing, but the record
        it opens is the submission — the negotiation against this listing, not
        the buyer's own file. A column headed Buyer that lands somewhere else is
        how a specialist ends up on the wrong record wondering where the buyer's
        phone number went.
      */
      key: "submission",
      header: "Submission",
      render: (s) =>
        onOpenSubmission ? (
          /*
            🔴 Rows OPEN, they do not navigate. This was a `<Link>`, and this
            body renders inside a drawer as often as it renders as a page: from
            a buyer's shortlist the click unmounted the buyer page AND the
            listing drawer sitting on it, and the only way back was the browser
            button.
          */
          <button
            type="button"
            onClick={() => onOpenSubmission(s.id, s.buyerDisplayName)}
            className="min-w-0 max-w-full truncate rounded-sm text-left text-gold-shadow hover:underline focus-visible:outline-none focus-visible:shadow-goldRing"
          >
            {s.buyerStaffDisplayName ?? s.buyerDisplayName}
          </button>
        ) : (
          // No drawer slot above us — we ARE the drawer. A new tab reaches the
          // submission while leaving the whole workspace, this drawer included,
          // exactly where it was.
          <a
            href={`/buyer-submissions/${s.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 max-w-full items-center gap-space-1 text-gold-shadow hover:underline"
          >
            <span className="truncate">{s.buyerStaffDisplayName ?? s.buyerDisplayName}</span>
            <ExternalLink size={12} strokeWidth={1.5} aria-hidden className="shrink-0" />
          </a>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => (
        <StatusBadge tone={submissionStatusTone(s.status)}>{buyerLabel(s.status)}</StatusBadge>
      ),
    },
    {
      key: "offer",
      header: "Deal structure price",
      align: "right",
      render: (s) => <TableValue value={money(s.dealStructurePrice)} />,
    },
    {
      key: "followUp",
      header: "Follow-up",
      render: (s) =>
        s.nextFollowUpDate ? <Timestamp value={s.nextFollowUpDate} /> : <MissingValue />,
    },
  ];
}

function money(value?: number | null) {
  return value == null ? null : formatCurrency(value);
}

/**
 * The listing's stored URL on its Source card (R3).
 *
 * <p>🔴 `rawUrl` is whatever a specialist pasted, so it goes through
 * {@link safeHref} before it becomes an `href` — a `javascript:` value rendered
 * as a link would run with the reader's session on click. An unsafe value is
 * still shown, as plain selectable text, so whoever pasted it can see it and
 * fix it. `safeHref` accepts `http:` as well as `https:`, which is right here
 * and deliberately wider than the rule for printing a page in an email
 * (`qualifyListingPageUrl`): a link a specialist opens is not a line Phoenix
 * sends under its own name.</p>
 */
function ListingUrlValue({ rawUrl }: { rawUrl: string | null | undefined }) {
  if (!rawUrl) return null;
  const href = safeHref(rawUrl);
  if (href == null) {
    return (
      <span className="break-all" data-testid="listing-url-text">
        {rawUrl}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-gold-shadow hover:underline break-all"
      data-testid="listing-url-link"
    >
      {rawUrl}
    </a>
  );
}

/** D20 — the phone with its line type, e.g. "(866) 426-8801 · Mobile". The
 *  type is shown only once it is known. */
function phoneWithType(agent: ListingAgentDto): string | null {
  if (!agent.phone) return null;
  const type = agent.phoneType && agent.phoneType !== "Unknown" ? agent.phoneType : null;
  return type ? `${agent.phone} · ${type}` : agent.phone;
}

/**
 * D20 — the listing's contact form row. The link goes through {@link safeHref}
 * like the Source card's URL; an edit is licensed here because the value it
 * changes is rendered beside it.
 */
function ContactFormField({
  url,
  onEdit,
}: {
  url?: string | null;
  onEdit?: () => void;
}) {
  const href = url ? safeHref(url) : null;
  return (
    <DetailField
      label="Contact form"
      wide
      value={
        <span className="flex flex-wrap items-center gap-space-2">
          {url ? (
            href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-shadow hover:underline break-all"
                data-testid="listing-contact-form-link"
              >
                {url}
              </a>
            ) : (
              <span className="break-all">{url}</span>
            )
          ) : (
            <span className="text-gray-mid">None recorded</span>
          )}
          {onEdit && (
            <Button
              variant="text"
              size="sm"
              onClick={onEdit}
              aria-label={url ? "Edit contact form" : "Add contact form"}
              data-testid="listing-contact-form-edit"
            >
              <Pencil size={14} strokeWidth={1.5} aria-hidden /> {url ? "Edit" : "Add"}
            </Button>
          )}
        </span>
      }
    />
  );
}
